import { useParams, useSearchParams } from "react-router-dom";
import { useSectionNavLinks } from "../_utils";
import AppLayout from "@/layouts/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import TabLayout from "./_TabLayout";
import Tabs from "@/components/Tabs";
import JobsTableLayout from "@/pages/_layouts/jobs/JobsTableLayout";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { SectionDetailsHeader } from "../_components/SectionDetailsHeader";
import PageTitle from "@/components/PageTitle";
import { useState, useEffect } from "react";
import PgaConfetti from "@/components/PgaConfetti";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Breadcrumb from "@/components/Breadcrumb";

export default function SectionJobPostsSearchPage() {
  const { sectionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(false);
  const { isCompactLayout: isMobile } = useIsCompactLayout();

  const location = useLocation();
  const { pathname } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const navLinks = useSectionNavLinks(sectionId);

  // Handle confetti trigger from URL
  useEffect(() => {
    const confettiParam = searchParams.get("confetti");
    if (confettiParam === "true") {
      setShowConfetti(true);
      // Remove the confetti parameter from URL
      searchParams.delete("confetti");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  if (!sectionId) {
    return (
      <div data-testid="error">
        <ErrorBoundary variant="inline" title="Invalid Page Access" message="Missing section identifier" />
      </div>
    );
  }

  // Confetti configuration parameters
  const numberOfPieces = isMobile ? 50 : 130;
  const xVelocityModifier = isMobile ? 100 : 200;
  const yVelocityModifier = isMobile ? 60 : 120;

  const renderDesktopLayout = () => (
    <>
      <SectionDetailsHeader sectionId={sectionId} />
      <Tabs
        tabItems={[
          {
            label: "Jobs",
            to: "/sections/:sectionId/jobs/list" as Path,
            active: currentUrl === `/sections/${sectionId}/jobs/list`
          }
        ].map(({ label, to, active }) => ({
          label,
          onClick: () => navigate(to, { params: { sectionId } }),
          active
        }))}
      />
      <JobsTableLayout
        tableHeading="PGA Job Board - Section Jobs"
        tableDescription="View and manage all jobs posted for Section facilities on the PGA job board"
        columnHeaders={{
          title: true,
          facility: true,
          serviceLevel: true,
          totalCompensation: false,
          employer: true,
          owner: false,
          postedOn: true,
          submissionDeadline: true,
          applicantCount: true,
          postingStage: true,
          jobStatus: true,
          section: false
        }}
        filters={{
          postingStage: true,
          jobStatus: true,
          serviceLevel: true,
          postedDateRange: true,
          section: false,
          owner: false
        }}
        defaultValues={{ sectionId: sectionId }}
        defaultSort={[{ id: "goLiveTime", desc: true }]}
      />
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        compactOnly
        compactTitle="Jobs"
        backLink={{ to: "/sections/:sectionId", params: { sectionId: sectionId! } }}
      />
      <SectionDetailsHeader sectionId={sectionId} />
      <Tabs
        tabItems={[
          {
            label: "Jobs",
            to: "/sections/:sectionId/jobs/list" as Path,
            active: currentUrl === `/sections/${sectionId}/jobs/list`
          }
        ].map(({ label, to, active }) => ({
          label,
          onClick: () => navigate(to, { params: { sectionId } }),
          active
        }))}
      />
      <JobsTableLayout
        tableHeading="PGA Job Board - Section Jobs"
        tableDescription="View and manage all jobs posted for Section facilities on the PGA job board"
        columnHeaders={{
          title: true,
          facility: true,
          serviceLevel: true,
          totalCompensation: false,
          employer: true,
          owner: false,
          postedOn: true,
          submissionDeadline: true,
          applicantCount: true,
          postingStage: true,
          jobStatus: true,
          section: false
        }}
        filters={{
          postingStage: true,
          jobStatus: true,
          serviceLevel: true,
          postedDateRange: true,
          section: false,
          owner: false
        }}
        defaultValues={{ sectionId: sectionId }}
        defaultSort={[{ id: "createdAt", desc: true }]}
      />
    </>
  );

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      <PageTitle pattern={location.pathname} title={`Sections | Jobs`} />
      {showConfetti && (
        <PgaConfetti
          run={showConfetti}
          recycle={false}
          numberOfPieces={numberOfPieces}
          wind={0}
          gravity={0.7}
          initialVelocityX={Math.random() * xVelocityModifier + Math.random() * yVelocityModifier}
          initialVelocityY={Math.random() * yVelocityModifier - Math.random() * xVelocityModifier}
          tweenDuration={0.4}
          variant="golf"
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
      <TabLayout>{isMobile ? renderMobileLayout() : renderDesktopLayout()}</TabLayout>
    </AppLayout>
  );
}
